import React from "react";
import { cn } from "@/lib/utils";
import { Scale, ArrowRight } from "lucide-react";
import type { WidgetAction } from "@/lib/mechanics/types";

interface MoralityWidgetProps {
  alignment_label?: string;
  alignment_score?: number; // -100 (evil) to +100 (good)
  lawful_score?: number;    // -100 (chaotic) to +100 (lawful)
  system?: string;          // spectrum | dual_axis | grey | faction_morality
  visible?: boolean;
  recent_shifts?: Array<{ reason: string; direction: string; magnitude: number }>;
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
}

function SpectrumBar({ value, leftLabel, rightLabel }: { value: number; leftLabel: string; rightLabel: string }) {
  const pct = Math.max(0, Math.min(100, (value + 100) / 2));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] text-muted-foreground font-medium">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      <div className="relative h-2.5 rounded-full bg-muted overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/60 via-muted to-emerald-500/60 rounded-full" />
        <div
          className="absolute top-0 h-full w-1.5 rounded-full bg-foreground border border-background shadow-md transition-all duration-500"
          style={{ left: `calc(${pct}% - 3px)` }}
        />
      </div>
    </div>
  );
}

export function MoralityWidget({
  alignment_label,
  alignment_score = 0,
  lawful_score = 0,
  system = "grey",
  visible = true,
  recent_shifts = [],
}: MoralityWidgetProps) {
  if (!visible) return null;

  const hasData = alignment_label || alignment_score !== 0 || lawful_score !== 0;

  return (
    <div className="space-y-4 p-1">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Scale className="h-4 w-4 text-primary" />
        <span>Moral Alignment</span>
      </div>

      {!hasData ? (
        <p className="text-xs text-muted-foreground italic">
          Your moral path is uncharted. Make choices to define your alignment…
        </p>
      ) : (
        <>
          {alignment_label && (
            <div className="text-center">
              <span className="text-sm font-bold text-primary">{alignment_label}</span>
            </div>
          )}

          {(system === "spectrum" || system === "dual_axis") && (
            <SpectrumBar value={alignment_score} leftLabel="Evil" rightLabel="Good" />
          )}

          {system === "dual_axis" && (
            <SpectrumBar value={lawful_score} leftLabel="Chaotic" rightLabel="Lawful" />
          )}

          {system === "grey" && (
            <p className="text-xs text-muted-foreground italic">
              There is no objective good or evil — only the consequences of your choices.
            </p>
          )}

          {system === "faction_morality" && (
            <p className="text-xs text-muted-foreground italic">
              Each faction judges you by their own moral code.
            </p>
          )}
        </>
      )}

      {recent_shifts.length > 0 && (
        <div className="space-y-1.5 border-t border-border pt-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recent Shifts</span>
          {recent_shifts.slice(-5).map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <ArrowRight className={cn("h-3 w-3 mt-0.5 shrink-0", s.direction === "good" || s.direction === "lawful" ? "text-emerald-500" : "text-red-500")} />
              <span>{s.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Compact status bar version */
export function MoralityStatus({ alignment_label, visible }: MoralityWidgetProps) {
  if (!visible || !alignment_label) return null;
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <Scale className="h-3 w-3" />
      <span>{alignment_label}</span>
    </div>
  );
}
